import { KeyRound, ShieldCheck, ShieldOff, Smartphone } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardSubtitle,
  CardTitle,
  FormField,
  Input,
  Modal,
  PageHeader,
} from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { useNotify } from '@/hooks/useNotify';
import { api } from '@/lib/api';
import { copyToClipboard, downloadText, formatDateTime } from '@/lib/format';

export function AccountPage() {
  const { user, refresh } = useAuth();
  const notify = useNotify();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);

  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);

  if (!user) return null;

  const changePassword = async (event: FormEvent) => {
    event.preventDefault();
    setPasswordBusy(true);
    try {
      await api.auth.changePassword(currentPassword, newPassword);
      notify.success('Passwort geaendert. Andere Sitzungen wurden abgemeldet.');
      setCurrentPassword('');
      setNewPassword('');
    } catch (cause) {
      notify.fromError(cause, 'Aenderung fehlgeschlagen');
    } finally {
      setPasswordBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Konto" subtitle="Zugangsdaten und Zwei-Faktor-Authentifizierung." />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card padding="none">
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <CardTitle>Profil</CardTitle>
            <ShieldCheck size={17} className="text-t2" />
          </div>
          <dl className="divide-y divide-line text-[13px]">
            <Row label="E-Mail-Adresse" value={user.email} />
            <Row label="Name" value={user.name ?? '-'} />
            <Row label="Rolle" value={user.role === 'ADMIN' ? 'Administrator' : 'Kunde'} />
            <Row label="Konto seit" value={formatDateTime(user.createdAt)} />
          </dl>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>Passwort aendern</CardTitle>
              <CardSubtitle>Alle anderen Sitzungen werden danach abgemeldet.</CardSubtitle>
            </div>
            <KeyRound size={17} className="text-t2" />
          </CardHeader>

          <form onSubmit={changePassword} className="space-y-4">
            <FormField label="Aktuelles Passwort" required>
              <Input
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            </FormField>
            <FormField label="Neues Passwort" required hint="Mindestens 10 Zeichen.">
              <Input
                type="password"
                autoComplete="new-password"
                required
                minLength={10}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </FormField>
            <Button type="submit" disabled={passwordBusy}>
              Passwort aendern
            </Button>
          </form>
        </Card>
      </div>

      <div className="mt-4">
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Zwei-Faktor-Authentifizierung</CardTitle>
              <CardSubtitle>Zusaetzlicher Schutz mit einer Authenticator-App (TOTP).</CardSubtitle>
            </div>
            <Smartphone size={17} className="text-t2" />
          </CardHeader>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span
                className="flex h-11 w-11 items-center justify-center rounded-[13px]"
                style={{
                  background: user.totpEnabled ? 'var(--ok-soft)' : 'var(--bg-3)',
                  color: user.totpEnabled ? 'var(--ok)' : 'var(--t2)',
                }}
              >
                {user.totpEnabled ? <ShieldCheck size={20} /> : <ShieldOff size={20} />}
              </span>
              <div>
                <p className="text-[13.5px] font-bold text-t0">
                  {user.totpEnabled ? 'Aktiv' : 'Nicht eingerichtet'}
                </p>
                <p className="text-[11.5px] text-t2">
                  {user.totpEnabled
                    ? 'Bei jeder Anmeldung wird ein Code abgefragt.'
                    : 'Empfohlen fuer Konten mit produktiven Instanzen.'}
                </p>
              </div>
            </div>

            {user.totpEnabled ? (
              <Button variant="danger" onClick={() => setDisableOpen(true)}>
                Deaktivieren
              </Button>
            ) : (
              <Button onClick={() => setSetupOpen(true)}>Einrichten</Button>
            )}
          </div>
        </Card>
      </div>

      <TotpSetupDialog
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        onDone={async () => {
          setSetupOpen(false);
          await refresh();
        }}
      />

      <TotpDisableDialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        onDone={async () => {
          setDisableOpen(false);
          notify.success('Zwei-Faktor-Authentifizierung deaktiviert.');
          await refresh();
        }}
      />
    </div>
  );
}

function TotpSetupDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const notify = useNotify();
  const [step, setStep] = useState<'start' | 'verify' | 'codes'>('start');
  const [secret, setSecret] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.auth.totpSetup();
      setSecret(result.secret);
      setOtpauthUri(result.otpauthUri);
      setStep('verify');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Einrichtung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.auth.totpEnable(code);
      setRecoveryCodes(result.recoveryCodes);
      setStep('codes');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Code konnte nicht bestaetigt werden');
    } finally {
      setBusy(false);
    }
  };

  const errorBox = error && (
    <div className="rounded-[var(--radius-vela-md)] border border-line bg-bad-soft px-3 py-2 text-[13px] text-bad">
      {error}
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title="Zwei-Faktor-Authentifizierung einrichten">
      {step === 'start' && (
        <div className="space-y-4">
          <p className="text-[13px] text-t1">
            Sie benoetigen eine Authenticator-App wie Aegis, 1Password oder Google Authenticator. Im
            naechsten Schritt erhalten Sie ein Geheimnis, das Sie dort hinterlegen.
          </p>
          {errorBox}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button onClick={start} disabled={busy}>
              Weiter
            </Button>
          </div>
        </div>
      )}

      {step === 'verify' && (
        <form onSubmit={verify} className="space-y-4">
          <div>
            <p className="mb-1.5 text-[12.5px] font-semibold text-t1">Geheimnis</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-[var(--radius-vela-md)] border border-line bg-bg-inset px-3 py-2 font-mono text-xs text-t1">
                {secret}
              </code>
              <Button
                type="button"
                variant="secondary"
                onClick={async () => {
                  const ok = await copyToClipboard(secret);
                  if (ok) notify.success('Geheimnis kopiert.');
                  else notify.error('Kopieren nicht moeglich.');
                }}
              >
                Kopieren
              </Button>
            </div>
            <p className="mt-2 text-[11px] break-all text-t2">{otpauthUri}</p>
          </div>

          <FormField label="Code aus der App" required>
            <Input
              inputMode="numeric"
              className="font-mono tracking-[0.3em]"
              placeholder="000000"
              required
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </FormField>

          {errorBox}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={busy}>
              Aktivieren
            </Button>
          </div>
        </form>
      )}

      {step === 'codes' && (
        <div className="space-y-4">
          <p className="text-[13px] text-t1">
            Bewahren Sie diese Wiederherstellungscodes sicher auf. Jeder Code funktioniert genau
            einmal und ersetzt den Code aus der App.
          </p>
          <div className="grid grid-cols-2 gap-2 rounded-[var(--radius-vela-md)] border border-line bg-bg-inset p-4">
            {recoveryCodes.map((recoveryCode) => (
              <code key={recoveryCode} className="font-mono text-[13px] text-t0">
                {recoveryCode}
              </code>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => downloadText('fluxpanel-recovery-codes.txt', recoveryCodes.join('\n'))}
            >
              Herunterladen
            </Button>
            <Button onClick={() => void onDone()}>Fertig</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function TotpDisableDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.auth.totpDisable(password);
      setPassword('');
      await onDone();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Deaktivierung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Zwei-Faktor-Authentifizierung deaktivieren" size="sm">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-[13px] text-t1">
          Bestaetigen Sie mit Ihrem Passwort. Danach genuegt zur Anmeldung wieder das Passwort allein.
        </p>
        <FormField label="Passwort" required>
          <Input
            type="password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </FormField>
        {error && (
          <div className="rounded-[var(--radius-vela-md)] border border-line bg-bad-soft px-3 py-2 text-[13px] text-bad">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" variant="danger" disabled={busy}>
            Deaktivieren
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3">
      <dt className="text-[11.5px] text-t2">{label}</dt>
      <dd className="truncate text-t0">{value}</dd>
    </div>
  );
}
